import React, { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../Components/utils/ShowToast';
import axios from "axios"

export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {

  const [isLogin, setIsLogin] = useState(null);
  const [isProfileIncomplete, setIsProfileIncomplete] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [isAdminLogin, setIsAdminLogin] = useState(null);
  const [admin, setAdmin] = useState(null);
  /**
   * `sellerMatrix` / `buyerMatrix` hold a rate per commission category; `seller`
   * and `buyer` are the product rates, kept as the default for the screens that
   * only ever show one headline percentage.
   */
  const [feesObj, setFeesObj] = React.useState({
    seller: 0,
    buyer: 0,
    sellerMatrix: {},
    buyerMatrix: {},
    planType: null,
    loaded: false,
  });
  const [payoutFeeApplies, setPayoutFeeApplies] = React.useState(false);
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(1);

  const [isTabletPro, setIsTabletPro] = React.useState(window.innerWidth <= 1090);
  const [isTablet, setIsTablet] = React.useState(window.innerWidth <= 900);
  const [isMobilePro, setIsMobilePro] = React.useState(window.innerWidth <= 650);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 500);
  const [isMobileMini, setIsMobileMini] = React.useState(window.innerWidth <= 400);

  React.useEffect(() => {

    const handleResize = () => {
      setIsTablet(window.innerWidth <= 900);
      setIsTabletPro(window.innerWidth <= 1090);
      setIsMobilePro(window.innerWidth <= 650);
      setIsMobile(window.innerWidth <= 500);
      setIsMobileMini(window.innerWidth <= 400);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchUserData = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/checkLogin`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (response.data.isLogin) {
          setIsLogin(true);
          setUser(response.data.user);
          setCurrency(response.data.user.currency);
          const u = response.data.user;
          const hasCountry = Boolean(u?.buyerAddress?.country?.name || u?.buyerAddress?.country?.label);
          const incomplete = !u.phone || !u.languages || !u.currency || !hasCountry;
          setIsProfileIncomplete(incomplete);
          return response.data.user;
        } else {
          setIsLogin(false);
          setUser(null);
          setIsProfileIncomplete(false);
        }
      } catch (error) {
        setIsLogin(false);
        setUser(null);
      }
    }
    else {
      setIsLogin(false);
      setUser(null);
      setIsProfileIncomplete(false);
    }
    return null;
  };

  const fetchAdminData = async () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/admin/checkAdminLogin`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (response.data.isLogin) {
          setIsAdminLogin(true);
          setAdmin(response.data.admin);
        } else {
          setIsAdminLogin(false);
          setAdmin(null);
        }
      } catch (error) {
        setIsAdminLogin(false);
        setAdmin(null);
        console.log(error);
      }
    }
    else {
      setIsAdminLogin(false);
      setAdmin(null);
    }
  };

  const fetchFees = async (localUser) => {
    try {
      const user = await localUser;
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/feesAndMembership`);
      const commissions = response.data.commissions || {};
      const buyerMatrix = commissions.buyer || {};
      const planType = user?.sellerId?.planType || null;

      // A user who is not a seller has no seller rate to show at all, so the
      // matrix stays empty rather than falling back to the free-seller rates.
      const sellerMatrix = planType === 'Paid'
        ? commissions.paidSeller || {}
        : planType === 'Free'
          ? commissions.freeSeller || {}
          : {};

      setFeesObj({
        seller: Number(sellerMatrix.product) || 0,
        buyer: Number(buyerMatrix.product) || 0,
        sellerMatrix,
        buyerMatrix,
        planType,
        loaded: true,
      });

      setPayoutFeeApplies(Boolean(response.data.payoutFeeApplies));

    } catch (error) {
      console.error("Failed to fetch fees", error);
    }
  };

  const updateCurrency = async (newCurrency) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/updateCurrency`,
        { currency: newCurrency },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setCurrency(newCurrency);
    } catch (error) {
      console.error('Failed to update currency', error);
      showToast('Failed to update currency', 'error');
    }
  };

  useEffect(() => {
    if (localStorage.getItem('loggedOut')) {
      showToast("Logged out Successfully!", "success");
      localStorage.removeItem('loggedOut');
    }
    const localUser = fetchUserData();
    fetchAdminData();
    fetchFees(localUser);

    const cachedRate = localStorage.getItem(`exchangeRate_${currency}`);
    const cachedTime = localStorage.getItem(`exchangeRateTime_${currency}`);

    if (cachedRate && cachedTime && Date.now() - cachedTime < 3600000) { // Cache for 1 hour
      setExchangeRate(parseFloat(cachedRate));
    } else {
      const fetchExchangeRate = async () => {
        if (currency === 'USD') {
          setExchangeRate(1);
          return;
        }
        try {
          const response = await axios.get(
            `https://api.exchangerate-api.com/v4/latest/USD?symbols=${currency}`
          );
          const rate = response.data.rates[currency];
          setExchangeRate(rate);
          localStorage.setItem(`exchangeRate_${currency}`, rate);
          localStorage.setItem(`exchangeRateTime_${currency}`, Date.now());
        } catch (error) {
          console.error('Failed to fetch exchange rate', error);
          setExchangeRate(cachedRate ? parseFloat(cachedRate) : 1); // Fallback
        }
      };

      fetchExchangeRate();
    }

  }, [currency]);

  const login = (token) => {
    localStorage.setItem('token', token);
    setIsLogin(true);
    fetchUserData();
    localStorage.removeItem('adminToken');
  };

  const adminLogin = (token) => {
    localStorage.setItem('adminToken', token);
    setIsAdminLogin(true);
    fetchAdminData();
    localStorage.removeItem('token');
    setIsLogin(false);
    setUser(null);
  };

  const logout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem('token');
      setIsLogin(false);
      setUser(null);
      localStorage.setItem("loggedOut", 'true');
      navigate("/");
      window.location.reload();
    }
  };

  const adminLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem('adminToken');
      setIsAdminLogin(false);
      setAdmin(null);
      localStorage.setItem("loggedOut", 'true');
      navigate("/chwi-admin/login");
      window.location.reload();
    }
  };

  return (
    <LoginContext.Provider value={{ isLogin, login, logout, fetchUserData, user, admin, adminLogin, adminLogout, fetchAdminData, isAdminLogin, feesObj, payoutFeeApplies, fetchFees, isTablet, isTabletPro, isMobile, isMobilePro, isMobileMini, currency, exchangeRate, updateCurrency, isProfileIncomplete }}>
      {children}
    </LoginContext.Provider>
  );
};